import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, Toggle, fmtN, fmtDateTime } from "@/components/primitives";

const HEALTH = {
  not_configured: ["Not configured", "var(--ui-text-lo)"],
  connected: ["Connected", "var(--sv-good)"],
  degraded: ["Degraded", "var(--sv-watch)"],
  disconnected: ["Disconnected", "var(--sv-crit)"],
};
const CHECK_DOT = { pass: "var(--sv-good)", warn: "var(--sv-watch)", fail: "var(--sv-crit)" };
const VERIFY_TONE = {
  PASS: ["var(--sv-good)", "var(--sv-good-bg)"],
  WARN: ["var(--sv-watch)", "var(--sv-watch-bg)"],
  FAIL: ["var(--sv-crit)", "var(--sv-crit-bg)"],
  INCONCLUSIVE: ["var(--ui-text-lo)", "var(--ui-card-2)"],
};

export function HealthPill({ state }) {
  const [label, color] = HEALTH[state] || HEALTH.not_configured;
  return (
    <span className="inline-flex items-center" data-testid="conn-health-pill"
      style={{ gap: 8, background: "var(--ui-card-2)", border: `1px solid ${color}`,
        color, borderRadius: 8, padding: "4px 12px", fontSize: 12, fontFamily: "'Inter',sans-serif", fontWeight: 500 }}>
      <span style={{ width: 7, height: 7, borderRadius: 9999, background: color }} />{label}
    </span>
  );
}

// Rich check-4 verification panel (counts, %, sample lines, diagnostic) --------
export function VerificationPanel({ v }) {
  if (!v) return null;
  const [color, bg] = VERIFY_TONE[v.status] || VERIFY_TONE.INCONCLUSIVE;
  const stat = (label, value) => (
    <div style={{ minWidth: 92 }}>
      <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>{label}</div>
      <div className="num" style={{ fontSize: 19, fontWeight: 700, color: "var(--ui-text-hi)", marginTop: 3 }}>{value}</div>
    </div>
  );
  return (
    <div data-testid="verification-panel" style={{ marginTop: 18, border: `1px solid ${color}`, borderRadius: 12, background: bg, padding: 18, position: "relative", overflow: "hidden" }}>
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: color }} />
      <div className="flex items-center" style={{ gap: 10, paddingLeft: 8 }}>
        <span data-testid="verification-status" className="num" style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.5px", color }}>{v.status}</span>
        {v.ranAt && <span className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)" }}>· verified {fmtDateTime(v.ranAt)}</span>}
      </div>
      <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-mid)", marginTop: 8, paddingLeft: 8 }}>{v.message}</div>

      <div className="flex" style={{ gap: 22, flexWrap: "wrap", marginTop: 16, paddingLeft: 8 }}>
        {stat("Invoices sampled", fmtN(v.invoicesSampled))}
        {stat("Consumable lines", fmtN(v.consumableLines ?? v.totalLineItems))}
        {stat("Catalogue-backed", fmtN(v.catalogBackedLines))}
        {stat("Free-text consumable", fmtN(v.freeTextConsumableLines ?? v.oneOffLines))}
        {stat("Catalogue %", `${v.catalogueBackedPct ?? 0}%`)}
        {stat("Free-text %", `${v.freeTextPct ?? 0}%`)}
      </div>
      <div className="prose-i" data-testid="verify-nonconsumable" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 10, paddingLeft: 8 }}>
        {v.catalogueBackedPct ?? 0}% of consumable lines catalogue-backed · {fmtN(v.nonConsumableLines ?? 0)} non-consumable lines excluded (freight, labour)
      </div>

      {(v.sampleLines || []).length > 0 && (
        <div style={{ marginTop: 16, paddingLeft: 8 }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>Sample lines — eyeball the bean products</div>
          <div style={{ border: "1px solid var(--ui-line-soft)", borderRadius: 8, overflow: "hidden" }}>
            {v.sampleLines.map((l, i) => (
              <div key={i} data-testid={`verify-sample-${i}`} className="flex items-center"
                style={{ gap: 12, padding: "8px 12px", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)", background: "var(--ui-card)" }}>
                <span className="num" style={{ fontSize: 12, color: l.partNo ? "var(--ui-text-hi)" : "var(--sv-watch)", minWidth: 96 }}>{l.partNo || "no part no"}</span>
                <span className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", flex: 1 }}>{l.description || "—"}</span>
                <span className="prose-i" style={{ fontSize: 10.5, color: "var(--ui-text-lo)", textTransform: "uppercase", letterSpacing: "0.4px" }}>{l.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {v.diagnostic && (
        <div style={{ marginTop: 16, paddingLeft: 8 }} data-testid="verify-diagnostic">
          <div className="uppercase-label" style={{ marginBottom: 8, color: "var(--sv-crit)" }}>Diagnostic — why it could not verify</div>
          <div style={{ border: "1px solid var(--ui-line-soft)", borderRadius: 8, background: "var(--ui-bg)", padding: 12 }}>
            <div className="num" style={{ fontSize: 12, color: "var(--ui-text-mid)" }}>GET {v.diagnostic.path || "—"}</div>
            <div className="num" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 4 }}>HTTP {v.diagnostic.httpStatus ?? "no response"}</div>
            {v.diagnostic.body && <div className="num" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 4, wordBreak: "break-all" }}>{v.diagnostic.body}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

const SCOPES = [["90d", "Last 90 days"], ["12m", "Last 12 months"], ["36m", "Last 36 months"], ["full", "Full history"]];
// Cost shown BEFORE triggering a multi-hour run (empirical: 12m = 6,820 calls / 76 min).
const SYNC_ESTIMATE = { "36m": "~17–21k API calls · ~3.5–4 hours", "full": "~30k API calls · ~5.5 hours" };

// Read-only invoice payload diagnostic — re-runnable shape investigation --------
function InvoiceDiagnostic() {
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const run = async () => {
    setBusy(true); setErr("");
    try { setD(await endpoints.diagnoseInvoices()); }
    catch (e) { setErr(e?.response?.data?.detail || "Diagnostic failed."); }
    finally { setBusy(false); }
  };
  const c = d?.classification?.counts;
  const kv = (label, value) => (
    <div style={{ minWidth: 84 }}>
      <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>{label}</div>
      <div className="num" style={{ fontSize: 17, fontWeight: 700, color: "var(--ui-text-hi)", marginTop: 3 }}>{value}</div>
    </div>
  );
  return (
    <div style={{ marginTop: 18, borderTop: "1px solid var(--ui-line-soft)", paddingTop: 16 }}>
      <div className="flex items-center" style={{ justifyContent: "space-between" }}>
        <div>
          <div className="num" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>Invoice payload diagnostic</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 2 }}>Read-only · dumps endpoint shapes &amp; job/recurring classification</div>
        </div>
        <Button variant="secondary" data-testid="run-invoice-diagnostic" onClick={run} disabled={busy}>{busy ? "Running…" : "Run invoice diagnostic"}</Button>
      </div>
      {err && <div className="prose-i" style={{ fontSize: 12.5, color: "var(--sv-crit)", marginTop: 10 }}>{err}</div>}
      {d && (
        <div data-testid="invoice-diagnostic-result" style={{ marginTop: 14 }}>
          <div className="flex" style={{ gap: 20, flexWrap: "wrap" }}>
            {Object.entries(d.endpoints || {}).map(([name, e]) => (
              <div key={name} className="num" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", background: "var(--ui-card-2)", border: "1px solid var(--ui-line-soft)", borderRadius: 8, padding: "8px 12px" }}>
                /{name}/ → HTTP <b style={{ color: e.httpStatus === 200 ? "var(--sv-good)" : "var(--sv-crit)" }}>{String(e.httpStatus)}</b>
              </div>
            ))}
          </div>
          {c && (
            <div className="flex" style={{ gap: 22, flexWrap: "wrap", marginTop: 14 }}>
              {kv("Sampled", c.sampled)}
              {kv("Job-based", c.job)}
              {kv("Recurring", c.recurring)}
              {kv("Unresolvable", c.unresolvable)}
              {kv("Multi-job", c.multiJob)}
            </div>
          )}
          {d.details?.jobInvoice && (
            <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)", marginTop: 14 }}>
              Job-invoice detail: HTTP {String(d.details.jobInvoice.httpStatus)} · line items present: {d.details.jobInvoice.hasCatalogs ? "yes (Catalogs)" : "no"}
            </div>
          )}
          <div className="num" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginTop: 8, wordBreak: "break-all" }}>list used: {d.classification?.listUsed}</div>
        </div>
      )}
    </div>
  );
}

// Live sync panel — bounded scope, 2s polling, cancellable, survives refresh -----
function SyncPanel({ demoMode }) {
  const qc = useQueryClient();
  const [scope, setScope] = useState("90d");
  const [err, setErr] = useState("");
  const { data: prog } = useQuery({
    queryKey: ["syncprog"],
    queryFn: endpoints.syncProgress,
    refetchInterval: (q) => {
      const r = q.state.data?.run;
      return r?.status === "running" && !r?.stalled ? 2000 : false;
    },
    refetchOnWindowFocus: false,
  });
  const run = prog?.run;
  const stalled = !!run?.stalled;
  const running = run?.status === "running" && !stalled;
  const interrupted = stalled || ["error", "cancelled"].includes(run?.status);
  const noWork = run?.status === "no_work";

  const start = async (confirmPurge = false, force = false) => {
    setErr("");
    try {
      const r = await endpoints.syncStart(scope, confirmPurge, force);
      if (r?.status === "needs_confirmation") {
        if (window.confirm(r.message)) return start(true, force);
        return;
      }
      qc.invalidateQueries({ queryKey: ["syncprog"] });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start the sync.");
    }
  };
  const forceResync = async () => {
    if (!window.confirm("Force a FULL re-sync? This ignores all checkpoints and re-fetches every record from Simpro (read-only). Use this after a mapper change or to pull a wider history. Continue?")) return;
    return start(false, true);
  };
  const cancel = async () => {
    try { await endpoints.syncCancel(); qc.invalidateQueries({ queryKey: ["syncprog"] }); } catch {}
  };
  const clearStuck = async () => {
    setErr("");
    try { await endpoints.syncClearStuck(); await qc.invalidateQueries(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not clear the stuck run."); }
  };
  const resume = async () => {
    setErr("");
    try { await endpoints.syncResume(); qc.invalidateQueries({ queryKey: ["syncprog"] }); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not resume the run."); }
  };

  const STATUS_TONE = { running: "var(--ac-indigo-hi)", success: "var(--sv-good)", no_work: "var(--sv-watch)", cancelled: "var(--sv-watch)", stalled: "var(--sv-crit)", error: "var(--sv-crit)" };
  const statusLabel = stalled ? "stalled" : (run?.status === "no_work" ? "no work" : run?.status);
  const inputStyle = { background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 10, padding: "10px 12px", color: "var(--ui-text-hi)", fontFamily: "'Inter', sans-serif", fontSize: 14 };

  return (
    <Card style={{ marginTop: 20, padding: 26 }}>
      <div className="flex items-center" style={{ justifyContent: "space-between" }}>
        <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Run a sync</div>
        {run && <span className="num" style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.5px", color: STATUS_TONE[statusLabel] || "var(--ui-text-lo)", textTransform: "uppercase" }} data-testid="sync-status">{statusLabel}</span>}
      </div>
      <div className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-lo)", marginTop: 4 }}>
        Start with a small window to validate the data shape before committing to a full backfill.
      </div>

      {interrupted && (
        <div data-testid="sync-stalled-banner" style={{ marginTop: 16, background: "var(--sv-crit-bg)", border: "1px solid var(--sv-crit)", borderRadius: 12, padding: "14px 16px", position: "relative", overflow: "hidden" }}>
          <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "var(--sv-crit)" }} />
          <div className="prose-i" style={{ fontSize: 13, fontWeight: 600, color: "var(--ui-text-hi)", paddingLeft: 8 }}>
            {stalled ? "Sync stalled" : run?.status === "cancelled" ? "Sync cancelled" : "Sync failed"}
            {run?.secondsSinceHeartbeat != null && stalled ? ` · no heartbeat for ${run.secondsSinceHeartbeat}s` : ""}
          </div>
          <div className="prose-i" data-testid="sync-error-text" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", marginTop: 6, paddingLeft: 8 }}>
            {run?.error || "The sync stopped before finishing. Resume to continue from the last checkpoint, or clear it and start fresh."}
          </div>
          <div className="flex" style={{ marginTop: 12, paddingLeft: 8, gap: 10 }}>
            <Button data-testid="sync-resume" onClick={resume}>Resume from checkpoint</Button>
            <Button variant="secondary" data-testid="sync-clear-stuck" onClick={clearStuck}>Clear stuck run</Button>
          </div>
        </div>
      )}

      {noWork && (
        <div data-testid="sync-nowork-banner" style={{ marginTop: 16, background: "var(--sv-watch-bg)", border: "1px solid var(--sv-watch)", borderRadius: 12, padding: "14px 16px", position: "relative", overflow: "hidden" }}>
          <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "var(--sv-watch)" }} />
          <div className="prose-i" style={{ fontSize: 13, fontWeight: 600, color: "var(--ui-text-hi)", paddingLeft: 8 }}>No new records</div>
          <div className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", marginTop: 6, paddingLeft: 8, lineHeight: 1.5 }}>
            {run?.note || "Nothing to fetch for this window."} Use “Force full re-sync” to re-fetch everything.
          </div>
        </div>
      )}

      <div className="flex items-center" style={{ gap: 12, marginTop: 16, flexWrap: "wrap" }}>
        <select data-testid="sync-scope" value={scope} onChange={(e) => setScope(e.target.value)} disabled={running} style={inputStyle}>
          {SCOPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {!running
          ? <>
              <Button data-testid="sync-start" onClick={() => start()}>Run sync now</Button>
              <Button variant="secondary" data-testid="sync-force" onClick={forceResync}>Force full re-sync</Button>
            </>
          : <Button variant="secondary" data-testid="sync-cancel" onClick={cancel}>Cancel sync</Button>}
        {!running && SYNC_ESTIMATE[scope] && (
          <span data-testid="sync-estimate" className="prose-i" style={{ fontSize: 12.5, color: "var(--sv-watch)", border: "1px solid var(--sv-watch)", background: "var(--sv-watch-bg)", borderRadius: 8, padding: "7px 11px", whiteSpace: "nowrap" }}>
            ⏱ {SYNC_ESTIMATE[scope]}
          </span>
        )}
      </div>
      <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 8 }}>
        Widening the window (e.g. 90 days → 12 months) automatically re-fetches the extra history. “Force full re-sync” ignores checkpoints entirely — use it to re-apply data fixes to already-synced records.
      </div>
      {err && <div className="prose-i" data-testid="sync-error" style={{ fontSize: 12.5, color: "var(--sv-crit)", marginTop: 12 }}>{err}</div>}

      {run && (
        <div style={{ marginTop: 18, borderTop: "1px solid var(--ui-line-soft)", paddingTop: 16 }}>
          <div className="flex" style={{ gap: 26, flexWrap: "wrap" }}>
            {[["Current entity", running ? (run.entity || "starting…") : "—"],
              ["Records processed", fmtN(run.recordCount)],
              ["API calls", fmtN(run.apiCalls)],
              ["Errors / skips", fmtN((run.skipped || []).reduce((a, s) => a + (s.count || 0), 0))]].map(([l, v]) => (
              <div key={l}>
                <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>{l}</div>
                <div className="num" style={{ fontSize: 18, fontWeight: 700, color: "var(--ui-text-hi)", marginTop: 3 }} data-testid={`sync-${l.split(" ")[0].toLowerCase()}`}>{v}</div>
              </div>
            ))}
          </div>

          {Object.keys(run.perEntity || {}).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className="uppercase-label" style={{ marginBottom: 8 }}>Records per entity</div>
              <div className="flex" style={{ gap: 10, flexWrap: "wrap" }}>
                {Object.entries(run.perEntity).map(([k, v]) => (
                  <span key={k} className="num" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", background: "var(--ui-card-2)", border: "1px solid var(--ui-line-soft)", borderRadius: 8, padding: "6px 10px" }}>
                    {k}: <b style={{ color: "var(--ui-text-hi)" }}>{fmtN(v)}</b>
                  </span>
                ))}
              </div>
            </div>
          )}

          {run.status === "success" && (
            <div data-testid="sync-summary" style={{ marginTop: 16, background: "var(--sv-good-bg)", border: "1px solid var(--sv-good)", borderRadius: 12, padding: "14px 16px" }}>
              <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-hi)", fontWeight: 600 }}>Sync complete · {run.scope === "full" ? "full history" : run.scope === "36m" ? "last 36 months" : run.scope === "12m" ? "last 12 months" : "last 90 days"}</div>
              <div className="prose-i" style={{ fontSize: 12.5, color: "var(--ui-text-mid)", marginTop: 6 }}>
                {fmtN(run.recordCount)} records · {fmtN(run.consumableLines)} consumable lines · {fmtN(run.beanAccounts)} accounts with bean purchases
                {run.earliestSyncedDate ? ` · history from ${fmtDateTime(run.earliestSyncedDate).split(",")[0]}` : ""}
              </div>
            </div>
          )}

          {(run.skipped || []).length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="uppercase-label" style={{ marginBottom: 6 }}>Records skipped</div>
              {run.skipped.map((s, i) => (
                <div key={i} className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>{s.entity}: {s.reason} ({fmtN(s.count)})</div>
              ))}
            </div>
          )}
        </div>
      )}
      {demoMode && (
        <div className="prose-i" style={{ fontSize: 12, color: "var(--sv-watch)", marginTop: 14 }}>
          Demo mode is on — a live sync is disabled until DEMO_MODE is set to false.
        </div>
      )}
    </Card>
  );
}

export default function Connection() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["connection"], queryFn: endpoints.getConnection });
  const [host, setHost] = useState("");
  const [company, setCompany] = useState("");
  const [token, setToken] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);

  const conn = data?.connection;
  const health = data?.health;
  const envManaged = conn?.envManaged;

  const [demoBusy, setDemoBusy] = useState(false);
  const [demoErr, setDemoErr] = useState("");
  const demoOn = !!health?.demoMode;
  const demoEnvManaged = !!health?.demoEnvManaged;
  const toggleDemo = async (next) => {
    setDemoErr(""); setDemoBusy(true);
    try {
      await endpoints.setDemoMode(next);
      await qc.invalidateQueries();
    } catch (e) {
      setDemoErr(e?.response?.data?.detail || "Could not change demo mode.");
    } finally { setDemoBusy(false); }
  };

  useEffect(() => {
    if (conn) { setHost(conn.simproHost || ""); setCompany(conn.simproCompanyId || ""); }
  }, [conn?.simproHost]);

  const runTest = async () => {
    if (host && token) { await endpoints.saveConnection({ simproHost: host, simproCompanyId: company, simproToken: token }); }
    else if (host !== conn?.simproHost) { await endpoints.saveConnection({ simproHost: host, simproCompanyId: company }); }
    setTesting(true);
    try { const r = await endpoints.testConnection(); setResult(r); await refetch(); }
    finally { setTesting(false); }
  };
  const save = async () => {
    const body = { simproHost: host, simproCompanyId: company };
    if (token) body.simproToken = token;
    await endpoints.saveConnection(body);
    setToken(""); setSaved(true); setTimeout(() => setSaved(false), 1600);
    await qc.invalidateQueries();
  };

  const inputStyle = { width: "100%", background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 10, padding: "11px 13px", color: "var(--ui-text-hi)", fontFamily: "'Inter', sans-serif", fontSize: 14, marginTop: 6 };
  const companies = result?.companies || [];
  const verification = result?.verification || health?.lastVerification || null;

  return (
    <div>
      <div className="flex" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.9px", color: "var(--ui-text-hi)" }}>Connection</div>
          <div className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-lo)", marginTop: 6 }}>Simpro credentials &amp; live connection health · read-only access</div>
        </div>
        <HealthPill state={health?.state} />
      </div>

      <div className="flex" style={{ gap: 20, alignItems: "flex-start" }}>
        {/* credentials + checks */}
        <Card style={{ flex: 1, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Simpro credentials</div>
          {envManaged && <div className="prose-i" style={{ fontSize: 12, color: "var(--sv-watch)", marginTop: 6 }}>Set via environment — read-only here.</div>}
          <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)", display: "block", marginTop: 14 }}>Simpro host
            <input data-testid="conn-host" value={host} onChange={(e) => setHost(e.target.value)} disabled={envManaged} style={inputStyle} placeholder="yourcompany.simprosuite.com" />
          </label>
          <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)", display: "block", marginTop: 14 }}>Company
            <select data-testid="conn-company" value={company} onChange={(e) => setCompany(e.target.value)} disabled={companies.length === 0} style={inputStyle}>
              <option value="">{companies.length ? "Select company…" : "Test connection first"}</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.id})</option>)}
            </select>
          </label>
          <label className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-mid)", display: "block", marginTop: 14 }}>API token (write-only)
            <input data-testid="conn-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} disabled={envManaged}
              style={inputStyle} placeholder={conn?.tokenMask || "Paste token"} />
          </label>
          {conn?.tokenMask && !token && <div className="num" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 6 }} data-testid="conn-token-mask">Stored: {conn.tokenMask}</div>}
          <div className="flex" style={{ gap: 12, marginTop: 20 }}>
            <Button data-testid="conn-test" onClick={runTest} disabled={testing || envManaged}>{testing ? "Testing…" : "Test connection"}</Button>
            <Button variant="secondary" data-testid="conn-save" onClick={save} disabled={envManaged}>{saved ? "Saved ✓" : "Save"}</Button>
          </div>

          {result && (
            <div style={{ marginTop: 20 }}>
              <div className="uppercase-label" style={{ marginBottom: 10 }}>Connection checks</div>
              {result.checks.map((c) => (
                <div key={c.key} className="flex" style={{ gap: 12, padding: "10px 0", borderTop: "1px solid var(--ui-line-soft)" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 9999, background: CHECK_DOT[c.status], marginTop: 5 }} />
                  <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-mid)" }}>{c.message}</div>
                </div>
              ))}
            </div>
          )}

          <VerificationPanel v={verification} />
          <InvoiceDiagnostic />
        </Card>

        {/* status */}
        <Card style={{ width: 340, padding: 26 }}>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Status</div>
          <div style={{ marginTop: 14 }}><HealthPill state={health?.state} /></div>
          {health?.reasons?.length > 0 && (
            <div className="prose-i" style={{ fontSize: 12.5, color: "var(--sv-watch)", marginTop: 10 }}>
              {health.reasons.join(" · ")}
            </div>
          )}
          {[["Last successful sync", fmtDateTime(health?.lastSuccessfulSync)],
            ["Last test run", fmtDateTime(health?.lastTestRun)],
            ["API calls (24h)", fmtN(health?.apiCalls24h)]].map(([l, v]) => (
            <div key={l} className="flex items-center" style={{ justifyContent: "space-between", padding: "12px 0", borderTop: "1px solid var(--ui-line-soft)" }}>
              <span className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)" }}>{l}</span>
              <span className="num" style={{ fontSize: 13.5, color: "var(--ui-text-hi)" }} data-testid={`status-${l.split(" ")[0].toLowerCase()}`}>{v}</span>
            </div>
          ))}

          {/* Data mode — DB-backed toggle; env value wins and locks the control */}
          <div style={{ padding: "14px 0", borderTop: "1px solid var(--ui-line-soft)" }}>
            <div className="flex items-center" style={{ justifyContent: "space-between" }}>
              <div>
                <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)" }}>Demo mode</div>
                <div className="num" style={{ fontSize: 13.5, color: "var(--ui-text-hi)", marginTop: 2 }} data-testid="status-demo">
                  {demoOn ? "On · seeded demo data" : "Off · live Simpro data"}
                </div>
              </div>
              <Toggle
                on={demoOn}
                disabled={demoEnvManaged || demoBusy}
                onChange={toggleDemo}
                data-testid="demo-toggle"
              />
            </div>
            {demoEnvManaged && (
              <div className="prose-i" data-testid="demo-env-note" style={{ fontSize: 12, color: "var(--sv-watch)", marginTop: 8 }}>
                Set via environment (DEMO_MODE) — read-only here. Change it in the backend .env to switch modes.
              </div>
            )}
            {!demoEnvManaged && (
              <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 8 }}>
                Demo mode loads seeded sample data. Turn it off to work against live Simpro data.
              </div>
            )}
            {demoErr && <div className="prose-i" data-testid="demo-error" style={{ fontSize: 12, color: "var(--sv-crit)", marginTop: 8 }}>{demoErr}</div>}
          </div>
        </Card>
      </div>

      <SyncPanel demoMode={health?.demoMode} />
    </div>
  );
}
